import { Injectable } from '@angular/core';

import { Automation, AutomationRun, ListRequest, ListResponse } from 'src/models';

import { GenericObjectsService } from './generic-objects.service';

@Injectable({
  providedIn: 'root',
})
export class AutomationsService extends GenericObjectsService<Automation> {
  protected baseUrl = '/automations';

  listRunsForAutomation(
    automationId: string | null,
    options: ListRequest = {},
  ): Promise<ListResponse<Partial<AutomationRun>>> {
    return this.apiService.postPromise(`${this.baseUrl}/${automationId}/runs/list`, options);
  }

  listRunsForConversation(
    conversationId: string,
    options: ListRequest = {},
  ): Promise<ListResponse<Partial<AutomationRun>>> {
    return this.apiService.postPromise(`${this.baseUrl}/conversations/${conversationId}/runs/list`, options);
  }

  runAutomationForConversation(automationId: string, conversationId: string, input: any = {}): Promise<any> {
    return this.apiService.postPromise(`${this.baseUrl}/${automationId}/conversations/${conversationId}/trigger`, {
      input,
    });
  }

  listLogEntriesForRun(automationId: string, automationRunId: string): Promise<any[]> {
    return this.apiService.getPromise(`${this.baseUrl}/${automationId}/runs/${automationRunId}/logs`);
  }

  getSecretNames(projectId: string | null): Promise<string[]> {
    return this.apiService.getPromise(`${this.baseUrl}/secrets`, {
      projectId: projectId ?? undefined,
    });
  }

  deleteSecret(secretName: string, projectId: string | null): Promise<void> {
    return this.apiService.deletePromise(`${this.baseUrl}/secrets/${secretName}`, {
      projectId: projectId ?? undefined,
    });
  }

  addSecret(name: string, value: string, projectId: string | null): Promise<void> {
    return this.apiService.putPromise(
      `${this.baseUrl}/secrets`,
      {
        name,
        value,
      },
      {
        projectId: projectId ?? undefined,
      },
    );
  }

  getAllFunctionsVersions(projectId: string | null) {
    return this.apiService.getPromise(`${this.baseUrl}/functions`, {
      projectId: projectId ?? undefined,
    });
  }

  getFunctions(version: string | number, projectId: string | null) {
    return this.apiService.getPromise(`${this.baseUrl}/functions/${version}`, {
      projectId: projectId ?? undefined,
    });
  }

  getStores(projectId: string | null) {
    return this.apiService.getPromise(`${this.baseUrl}/stores`, {
      projectId: projectId ?? undefined,
    });
  }

  addStore({ name, description }: { name: string; description: string }, projectId: string | null) {
    return this.apiService.postPromise(
      `${this.baseUrl}/stores`,
      {
        name,
        description,
      },
      {
        projectId: projectId ?? undefined,
      },
    );
  }

  deleteStore(storeName: string, projectId: string | null) {
    return this.apiService.deletePromise(`${this.baseUrl}/stores/${storeName}`, {
      projectId: projectId ?? undefined,
    });
  }

  getAutomationMetrics(
    projectId: string | null,
    dateRange: { startDate: string; endDate: string },
    status: string,
    automationId: string | null,
  ): Promise<any[]> {
    // Map 'success' filter to 'completed' status and handle 'all' filter
    const mappedStatus = status === 'success' ? 'completed' : status === 'all' ? undefined : status;
    const mappedAutomationId = automationId === 'all' || automationId === null ? undefined : automationId;

    const payload: any = {
      projectId,
      ...dateRange,
    };

    // Only include status and automationId if they have actual values
    if (mappedStatus) {
      payload.status = mappedStatus;
    }
    if (mappedAutomationId) {
      payload.automationId = mappedAutomationId;
    }

    return this.apiService.postPromise(`${this.baseUrl}/metrics`, payload);
  }
}
